import { useMemo } from 'react';

import {
  ScalingPolicyWithWorkloads,
  useScalingPoliciesWithWorkloads,
} from '@cast/features-cluster-shared';

export const TOP_SCALING_POLICIES_LIMIT = 3;
const TOP_POLICIES_PER_CATEGORY_LIMIT = 5;

export type ScalingPoliciesCardItem = {
  key: string;
  label: string;
  value: number;
  isSystemPolicy: boolean;
  isOpsPilot: boolean;
};

export type ScalingPolicyCategoryKey = 'system' | 'ops' | 'custom';

export type ScalingPolicyCategory = {
  key: ScalingPolicyCategoryKey;
  label: string;
  count: number;
  workloadCount: number;
  percentageOfWorkloads: number;
  topPolicies: ScalingPoliciesCardItem[];
};

type ScalingPoliciesCardData = {
  total: number;
  totalWorkloads: number;
  categories: ScalingPolicyCategory[];
  items: ScalingPoliciesCardItem[];
};

const sortByWorkloadCount = (
  a: ScalingPolicyWithWorkloads,
  b: ScalingPolicyWithWorkloads
) => b.workloads.length - a.workloads.length;

const getPolicyCategory = (
  policy: ScalingPolicyWithWorkloads
): ScalingPolicyCategoryKey => {
  if (policy.isReadonly && !policy.isCastware) {
    return 'system';
  }

  if (policy.isOpsPilot) {
    return 'ops';
  }

  return 'custom';
};

const CATEGORY_LABELS: Record<ScalingPolicyCategoryKey, string> = {
  system: 'System policies',
  ops: 'OpsPilot policies',
  custom: 'Custom policies',
};

export const useScalingPoliciesCardData = () => {
  const {
    policies,
    isLoading,
    isFetching,
    hasError: _hasError,
    refetch,
  } = useScalingPoliciesWithWorkloads();

  const data: ScalingPoliciesCardData | undefined = useMemo(() => {
    if (!policies) {
      return undefined;
    }

    const totalWorkloads = policies.reduce(
      (sum, policy) => sum + policy.workloads.length,
      0
    );

    const policiesByCategory = policies.reduce<
      Record<ScalingPolicyCategoryKey, ScalingPolicyWithWorkloads[]>
    >(
      (acc, policy) => {
        const category = getPolicyCategory(policy);
        acc[category].push(policy);
        return acc;
      },
      { system: [], ops: [], custom: [] }
    );

    const categories: ScalingPolicyCategory[] = (
      ['system', 'ops', 'custom'] as ScalingPolicyCategoryKey[]
    ).map((key) => {
      const categoryPolicies = policiesByCategory[key];
      const workloadCount = categoryPolicies.reduce(
        (sum, policy) => sum + policy.workloads.length,
        0
      );

      return {
        key,
        label: CATEGORY_LABELS[key],
        count: categoryPolicies.length,
        workloadCount,
        percentageOfWorkloads:
          totalWorkloads > 0 ? (workloadCount / totalWorkloads) * 100 : 0,
        topPolicies: categoryPolicies
          .slice()
          .sort(sortByWorkloadCount)
          .slice(0, TOP_POLICIES_PER_CATEGORY_LIMIT)
          .map((policy) => ({
            key: policy.id,
            label: policy.name,
            value: policy.workloads.length,
            isSystemPolicy: key === 'system',
            isOpsPilot: key === 'ops',
          })),
      };
    });

    const topPolicies = policies
      .slice()
      .sort(sortByWorkloadCount)
      .slice(0, TOP_SCALING_POLICIES_LIMIT);

    return {
      total: policies.length,
      totalWorkloads,
      categories,
      items: topPolicies.map((policy) => ({
        key: policy.id,
        label: policy.name,
        value: policy.workloads.length,
        isSystemPolicy: policy.isReadonly && !policy.isCastware,
        isOpsPilot: policy.isOpsPilot,
      })),
    };
  }, [policies]);

  return {
    data,
    isLoading,
    isFetching,
    hasError: _hasError,
    refetch,
  };
};
